import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControlLabel, Switch, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  PictureAsPdf as PreviewIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import { formatDateTime } from '../formatDate';
import { saveBlob, blobErrorBody } from '../downloadFile';

const SERVICE_MODES = ['temporary_services', 'outsourcing', 'any'];
const CONTRACT_KINDS = ['fixed_term', 'indefinite', 'work_or_task', 'any'];
const GROUPS = ['trabajador', 'empleador', 'cliente', 'cargo', 'jornada', 'lugar', 'contrato', 'remuneracion', 'documento'];
const MARKER = '{{...}}';
const DOCX_ACCEPT = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const slug = (value) => String(value || 'plantilla').normalize('NFD').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * Configuración ERP → Plantillas (PLAN_ERP.md §15, fase B1, 2026-10-19): el
 * contrato tipo en Word con marcadores {{grupo.campo}}. Subir, reemplazar el
 * Word, editar modalidad / tipo de contrato / estado, vista previa con datos de
 * ejemplo y descarga del Word original. Una activa por combinación.
 */
const ErpDocumentTemplatesTab = () => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };
  const k = (key) => `erp.documentTemplates.${key}`;

  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: create|replace|edit, template, form, file, error }
  const [working, setWorking] = useState(false);
  const [markersOpen, setMarkersOpen] = useState(false); // abierto de entrada si aún no hay plantillas

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, catalog] = await Promise.all([
        api.get('/erp/document-templates', { params: { organizationId: organization }, ...headers }),
        api.get('/erp/document-templates/placeholders', { params: { organizationId: organization }, ...headers }),
      ]);
      const items = list.data.response?.data || [];
      setTemplates(items);
      setPlaceholders(catalog.data.response?.data || []);
      if (items.length === 0) setMarkersOpen(true);
    } catch (err) {
      console.error(err);
      setError(t(k('loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const openDialog = (mode, template = null) =>
    setDialog({
      mode,
      template,
      file: null,
      error: null,
      form: {
        name: template?.name || '',
        serviceMode: template?.serviceMode || 'temporary_services',
        contractKind: template?.contractKind || 'fixed_term',
        active: template ? template.status === 'active' : true,
      },
    });

  const setForm = (field, value) => setDialog((d) => ({ ...d, form: { ...d.form, [field]: value }, error: null }));

  const errorText = (data) => {
    const code = data?.code;
    if (code === 'UNKNOWN_PLACEHOLDERS') {
      return t(k('errors.UNKNOWN_PLACEHOLDERS'), { list: (data.unknown || []).map((key) => `{{${key}}}`).join(', ') });
    }
    return code && t(k(`errors.${code}`), { defaultValue: '' }) ? t(k(`errors.${code}`)) : t(k('saveError'));
  };

  const canSubmit = Boolean(dialog) && (
    (dialog.mode === 'create' && dialog.form.name.trim() && dialog.file) ||
    (dialog.mode === 'replace' && dialog.file) ||
    (dialog.mode === 'edit' && dialog.form.name.trim())
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const { mode, template, form, file } = dialog;
    try {
      setWorking(true);
      let response;
      if (mode === 'edit') {
        response = await api.put(
          `/erp/document-templates/${template.uid}`,
          {
            organizationId: organization,
            name: form.name.trim(),
            serviceMode: form.serviceMode,
            contractKind: form.contractKind,
            status: form.active ? 'active' : 'inactive',
          },
          headers
        );
      } else {
        // Multipart: organizationId va en la query (el backend lo valida antes de leer el archivo)
        const body = new FormData();
        if (mode === 'create') {
          body.append('name', form.name.trim());
          body.append('serviceMode', form.serviceMode);
          body.append('contractKind', form.contractKind);
        }
        body.append('file', file);
        const url = mode === 'create' ? '/erp/document-templates' : `/erp/document-templates/${template.uid}/file`;
        const send = mode === 'create' ? api.post : api.put;
        response = await send(url, body, { params: { organizationId: organization }, ...headers });
      }
      const data = response.data.response || {};
      if (data.status_response === 'OK') {
        showAlert(t(k(mode === 'create' ? 'uploadSuccess' : 'saveSuccess')), 'success');
        if ((data.warnings || []).includes('NO_PLACEHOLDERS')) showAlert(t(k('noPlaceholdersWarning'), { marker: MARKER }), 'warning');
        else if ((data.deactivated || []).length > 0) showAlert(t(k('deactivatedInfo')), 'info');
        setDialog(null);
        load();
      }
    } catch (err) {
      console.error(err);
      const data = await blobErrorBody(err);
      setDialog((d) => (d ? { ...d, error: errorText(data) } : d));
    } finally {
      setWorking(false);
    }
  };

  const handlePreview = async (template) => {
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/document-templates/${template.uid}/preview`,
        { organizationId: organization },
        { ...headers, responseType: 'blob' }
      );
      saveBlob(response.data, `vista_previa_${slug(template.name)}.pdf`, 'application/pdf');
    } catch (err) {
      console.error(err);
      showAlert(t(k('previewError')), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleDownload = async (template) => {
    try {
      setWorking(true);
      const response = await api.get(`/erp/document-templates/${template.uid}/file`, {
        params: { organizationId: organization },
        ...headers,
        responseType: 'blob',
      });
      saveBlob(response.data, template.sourceFile?.name || `${slug(template.name)}.docx`);
    } catch (err) {
      console.error(err);
      showAlert(t(k('downloadError')), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async (key) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`);
      showAlert(t(k('placeholders.copied')), 'success');
    } catch (err) {
      console.error(err);
    }
  };

  const grouped = GROUPS.map((group) => ({ group, items: placeholders.filter((p) => p.group === group) })).filter((g) => g.items.length > 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="h6" fontWeight={700}>{t(k('title'))}</Typography>
          <Typography variant="body2" color="text.secondary">{t(k('subtitle'), { marker: MARKER })}</Typography>
        </Box>
        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => openDialog('create')} sx={{ textTransform: 'none' }}>
          {t(k('upload'))}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : templates.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>{t(k('empty'))}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(k('table.name'))}</TableCell>
                <TableCell>{t(k('table.serviceMode'))}</TableCell>
                <TableCell>{t(k('table.contractKind'))}</TableCell>
                <TableCell>{t(k('table.placeholders'))}</TableCell>
                <TableCell>{t(k('table.status'))}</TableCell>
                <TableCell>{t(k('table.updated'))}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.uid}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {template.name}
                    {(template.conversionMessages || []).length > 0 && (
                      <Tooltip title={`${t(k('conversionNotes'))}: ${template.conversionMessages.slice(0, 5).join(' · ')}`}>
                        <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {template.sourceFile?.name || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{t(k(`serviceModes.${template.serviceMode}`))}</TableCell>
                  <TableCell>{t(k(`contractKinds.${template.contractKind}`))}</TableCell>
                  <TableCell>{(template.placeholders || []).length}</TableCell>
                  <TableCell>
                    <Chip size="small" color={template.status === 'active' ? 'success' : 'default'}
                      label={t(k(template.status === 'active' ? 'active' : 'inactive'))} />
                  </TableCell>
                  <TableCell>{formatDateTime(template.modifiedAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={t(k('preview'))}>
                      <span><IconButton size="small" aria-label={t(k('preview'))} disabled={working} onClick={() => handlePreview(template)}><PreviewIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title={t(k('download'))}>
                      <span><IconButton size="small" aria-label={t(k('download'))} disabled={working} onClick={() => handleDownload(template)}><DownloadIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title={t(k('replace'))}>
                      <span><IconButton size="small" aria-label={t(k('replace'))} disabled={working} onClick={() => openDialog('replace', template)}><UploadIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title={t(k('edit'))}>
                      <span><IconButton size="small" aria-label={t(k('edit'))} disabled={working} onClick={() => openDialog('edit', template)}><EditIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Marcadores disponibles */}
      <Accordion expanded={markersOpen} onChange={(e, open) => setMarkersOpen(open)} disableGutters elevation={0}
        sx={{ border: '1px solid #E5E7EB', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={700}>{t(k('placeholders.title'))}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>{t(k('placeholders.intro'), { example: '{{trabajador.rut}}' })}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t(k('placeholders.format'))}</Typography>
          {grouped.map(({ group, items }) => (
            <Box key={group} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{t(k(`placeholders.groups.${group}`))}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '38%' }}>{t(k('placeholders.key'))}</TableCell>
                    <TableCell>{t(k('placeholders.label'))}</TableCell>
                    <TableCell>{t(k('placeholders.example'))}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((p) => (
                    <TableRow key={p.key}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {`{{${p.key}}}`}
                        <Tooltip title={t(k('placeholders.copy'))}>
                          <IconButton size="small" onClick={() => handleCopy(p.key)} aria-label={`${t(k('placeholders.copy'))} ${p.key}`}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {p.label}
                        {p.optional && <Chip size="small" variant="outlined" label={t(k('placeholders.optional'))} sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{p.example}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Subir / reemplazar / editar */}
      <Dialog open={Boolean(dialog)} onClose={() => !working && setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.mode === 'create' && t(k('uploadTitle'))}
          {dialog?.mode === 'replace' && t(k('replaceTitle'), { name: dialog.template?.name })}
          {dialog?.mode === 'edit' && t(k('editTitle'))}
        </DialogTitle>
        <DialogContent>
          {dialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {dialog.mode !== 'replace' && (
                <>
                  <TextField size="small" fullWidth label={t(k('name'))} value={dialog.form.name}
                    onChange={(e) => setForm('name', e.target.value)} inputProps={{ maxLength: 120 }} />
                  <TextField select size="small" fullWidth label={t(k('serviceMode'))} value={dialog.form.serviceMode}
                    onChange={(e) => setForm('serviceMode', e.target.value)}>
                    {SERVICE_MODES.map((mode) => <MenuItem key={mode} value={mode}>{t(k(`serviceModes.${mode}`))}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" fullWidth label={t(k('contractKind'))} value={dialog.form.contractKind}
                    onChange={(e) => setForm('contractKind', e.target.value)}>
                    {CONTRACT_KINDS.map((kind) => <MenuItem key={kind} value={kind}>{t(k(`contractKinds.${kind}`))}</MenuItem>)}
                  </TextField>
                </>
              )}
              {dialog.mode === 'edit' ? (
                <FormControlLabel
                  control={<Switch checked={dialog.form.active} onChange={(e) => setForm('active', e.target.checked)} />}
                  label={`${t(k('activeLabel'))} — ${t(k('activeHint'))}`} />
              ) : (
                <Box>
                  <Button component="label" variant="outlined" startIcon={<UploadIcon />} sx={{ textTransform: 'none' }}>
                    {t(k('chooseFile'))}
                    <input hidden type="file" accept={DOCX_ACCEPT} data-testid="template-file"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        e.target.value = ''; // permite volver a elegir el mismo archivo tras un 422
                        setDialog((d) => (d ? { ...d, file, error: null } : d));
                      }} />
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {dialog.file ? dialog.file.name : t(k('noFile'))}
                  </Typography>
                </Box>
              )}
              {dialog.error && <Alert severity="error">{dialog.error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={working} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={working || !canSubmit} sx={{ textTransform: 'none' }}>
            {working ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpDocumentTemplatesTab;
